import React, { useState, useEffect, KeyboardEvent } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { calculateTypabilityScore, getScoreTier, ScoreBreakdown as ScoreBreakdownType } from '@/utils/scoreCalculator';
import ScoreGauge from '@/components/ScoreGauge';
import KeyboardDisplay from '@/components/KeyboardDisplay';
import ScoreBreakdown from '@/components/ScoreBreakdown';
import HistoryList, { DomainScore } from '@/components/HistoryList';
import { AuthForms } from '@/components/AuthForms';
import UserNav from '@/components/UserNav';
import { User } from '@/types/user';
import { toast } from 'sonner';
import { useIsMobile } from '@/hooks/use-mobile';

// Key for storing global domain history in localStorage
const GLOBAL_HISTORY_KEY = 'domain_analysis_global_history';

const Index = () => {
  const isMobile = useIsMobile();
  const [domain, setDomain] = useState('');
  const [displayDomain, setDisplayDomain] = useState('');
  const [scoreBreakdown, setScoreBreakdown] = useState<ScoreBreakdownType | null>(null);
  const [history, setHistory] = useState<DomainScore[]>([]);
  const [activeTab, setActiveTab] = useState('keyboard');
  const [user, setUser] = useState<User | null>(null);
  const [showAuthForm, setShowAuthForm] = useState(false);
  
  // Check for stored user and load history on mount
  useEffect(() => {
    const storedUser = localStorage.getItem('user') || sessionStorage.getItem('user');
    
    if (storedUser) {
      const parsedUser = JSON.parse(storedUser);
      setUser(parsedUser);
      
      // Load saved history for this user
      const savedHistory = localStorage.getItem(`history_${parsedUser.id}`);
      if (savedHistory) {
        setHistory(JSON.parse(savedHistory));
      }
    } else {
      // If no user, load global history
      const globalHistory = localStorage.getItem(GLOBAL_HISTORY_KEY);
      if (globalHistory) {
        setHistory(JSON.parse(globalHistory));
      }
    }
  }, []);
  
  // Calculate score when domain changes
  useEffect(() => {
    if (domain) {
      setDisplayDomain(domain.toLowerCase());
      const result = calculateTypabilityScore(domain);
      setScoreBreakdown(result);
    } else {
      setScoreBreakdown(null);
    }
  }, [domain]);
  
  // Save history to localStorage whenever it changes
  useEffect(() => {
    if (history.length > 0) {
      if (user) {
        // Save user-specific history
        localStorage.setItem(`history_${user.id}`, JSON.stringify(history));
      } else {
        // Save to global history if no user
        localStorage.setItem(GLOBAL_HISTORY_KEY, JSON.stringify(history));
      }
    }
  }, [history, user]);
  
  // Save to history when analyze button is clicked
  const handleAnalyze = () => {
    if (!domain || !scoreBreakdown) return;
    
    // Add to history (avoid duplicates)
    const isDuplicate = history.some(item => item.domain.toLowerCase() === domain.toLowerCase());
    
    if (!isDuplicate) {
      const newHistory = [
        { 
          domain: domain.toLowerCase(), 
          score: scoreBreakdown.total,
          timestamp: Date.now() 
        },
        ...history
      ].slice(0, 10); // Keep only 10 most recent
      
      setHistory(newHistory);
      
      // If user isn't logged in, suggest creating an account to save history
      if (!user && history.length === 0) {
        toast(
          "Create an account to save your domain analyses", 
          {
            action: {
              label: "Sign Up",
              onClick: () => setShowAuthForm(true)
            },
          }
        );
      }
    }
  };
  
  // Select domain from history
  const handleSelectFromHistory = (selectedDomain: string) => {
    setDomain(selectedDomain);
  };
  
  // Clear history
  const handleClearHistory = () => {
    setHistory([]);
    if (user) {
      localStorage.removeItem(`history_${user.id}`);
    } else {
      localStorage.removeItem(GLOBAL_HISTORY_KEY);
    }
  };
  
  const handleKeyPress = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleAnalyze();
    }
  };
  
  const handleLogin = (loggedInUser: User) => {
    setUser(loggedInUser);
    setShowAuthForm(false);
    
    // Load any saved history for this user
    const savedHistory = localStorage.getItem(`history_${loggedInUser.id}`);
    if (savedHistory) {
      setHistory(JSON.parse(savedHistory));
    }
  };
  
  const handleLogout = () => {
    setUser(null);
    // After logout, switch to global history
    const globalHistory = localStorage.getItem(GLOBAL_HISTORY_KEY);
    if (globalHistory) {
      setHistory(JSON.parse(globalHistory));
    } else {
      setHistory([]);
    }
  };
  
  // Get score tier information
  const scoreTier = scoreBreakdown ? getScoreTier(scoreBreakdown.total) : null;

  return (
    <div className="min-h-screen py-4 md:py-8 bg-gradient-to-b from-blue-50 to-white">
      <div className="container mx-auto px-3 md:px-4">
        <header className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 md:mb-8">
          <div className="text-center md:text-left mb-4 md:mb-0">
            <h1 className="text-2xl md:text-4xl font-bold text-blue-900 mb-1 md:mb-2">Domain Typability Score</h1>
            <p className="text-sm md:text-xl text-blue-700">
              Analyze how easy a domain name is to type
            </p>
          </div>
          
          <div className="flex justify-center md:justify-end">
            <UserNav 
              user={user} 
              onLogout={handleLogout} 
              onShowAuth={() => setShowAuthForm(true)} 
            />
          </div>
        </header>
        
        <div className="max-w-4xl mx-auto">
          {showAuthForm ? (
            <AuthForms onLogin={handleLogin} />
          ) : (
            <Card className="shadow-lg">
              <CardHeader className="flex flex-row justify-between items-center">
                <div>
                  <CardTitle className="text-xl md:text-2xl">Domain Analyzer</CardTitle>
                  <CardDescription>
                    Enter a domain name to analyze its typability score
                  </CardDescription>
                </div>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                  <Input
                    type="text"
                    placeholder="Enter domain (e.g., google.com)"
                    value={domain}
                    onChange={(e) => setDomain(e.target.value)}
                    onKeyDown={handleKeyPress}
                    className="text-base md:text-lg"
                  />
                  <Button onClick={handleAnalyze} className="whitespace-nowrap">Analyze</Button>
                </div>
                
                <AnimatePresence>
                  {scoreBreakdown && (
                    <motion.div
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -20 }}
                      className="grid grid-cols-1 gap-6"
                    >
                      <div className="flex flex-col items-center justify-center bg-white rounded-lg p-4 shadow-sm">
                        <ScoreGauge score={scoreBreakdown.total} size={isMobile ? "md" : "lg"} />
                        <h3 className="text-lg md:text-xl font-bold mt-3 md:mt-4 text-center break-all">{displayDomain}</h3>
                        <div className="flex items-center mt-2 text-base md:text-lg font-medium">
                          <span className="mr-2 text-xl md:text-2xl">{scoreTier?.emoji}</span>
                          <span>{scoreTier?.tier}</span>
                        </div>
                      </div>
                      
                      <div>
                        <Tabs value={activeTab} onValueChange={setActiveTab}>
                          <TabsList className="grid grid-cols-2 mb-4">
                            <TabsTrigger value="keyboard">Keyboard Map</TabsTrigger>
                            <TabsTrigger value="breakdown">Score Breakdown</TabsTrigger>
                          </TabsList>
                          
                          <TabsContent value="keyboard" className="m-0">
                            <div className="overflow-x-auto">
                              <KeyboardDisplay domain={displayDomain} />
                            </div>
                          </TabsContent>
                          
                          <TabsContent value="breakdown" className="m-0">
                            <ScoreBreakdown breakdown={scoreBreakdown} />
                          </TabsContent>
                        </Tabs>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </CardContent>
            </Card>
          )}
          
          <div className="mt-6 md:mt-8">
            <HistoryList 
              history={history} 
              onSelect={handleSelectFromHistory} 
              onClear={handleClearHistory}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Index;
